// Service worker registration + update detection (PRD Module 7.7).
//
// The worker intentionally does NOT skipWaiting on install (see public/sw.js),
// so a new build sits in the "waiting" state until the user accepts it. This
// module surfaces that state to the UI and performs the handover on request.

type UpdateCallback = (apply: () => void) => void;

let waitingWorker: ServiceWorker | null = null;
let reloading = false;

/**
 * Hands control to the waiting worker and reloads once it takes over.
 * The reload is driven by `controllerchange` rather than called directly so we
 * only reload after the new worker is actually in control — reloading sooner
 * just re-runs the old build.
 */
function applyUpdate() {
  if (!waitingWorker) return;
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    }).catch(() => {});

    if ('caches' in window) {
      caches.keys().then((keys) => {
        for (const key of keys) {
          caches.delete(key);
        }
      });
    }
  });
}

// --- Install prompt (Add to Home Screen) ------------------------------------

// Not in the TS DOM lib; Chromium-only. Safari/Firefox never fire this event,
// so the install banner simply never appears there.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function watchInstallPrompt(onAvailable: (show: () => Promise<void>) => void) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // Suppress the browser's own mini-infobar; we show our own.
    deferredPrompt = e as BeforeInstallPromptEvent;
    onAvailable(async () => {
      if (!deferredPrompt) return;
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      deferredPrompt = null; // The event is single-use.
    });
  });
}
